import { useState } from "react";

import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";

import type { Message } from "api/services/messaging/schemas/messaging";
import { isTranslatedActivity } from "routes/patients/utils/activityHelpers";
import type { PreviewActivity } from "routes/patients/views/Treatment/components/TreatmentEditor/Modals";
import { ActivityPreviewModal } from "routes/patients/views/Treatment/components/TreatmentEditor/Modals";
import { BaseButton } from "shared/atoms/BaseButton";
import { RelativeDatetime } from "shared/atoms/RelativeDatetime";

import { ActivityIcon } from "./ActivityIcon";
import { ChatBubble } from "./styles";

function getActivityType(activityName: string | undefined) {
  switch (activityName) {
    case "exercise":
      return "exercise";
    case "followup_health_report":
      return "report";
    case "w1_health_report":
      return "report";
    case "weekly_health_report":
      return "report";
    default:
      return "report";
  }
}

interface ActivityMessageProps {
  message: Message;
}

export const ActivityMessage: React.FC<ActivityMessageProps> = ({ message }) => {
  const { t } = useTranslation();
  const [previewActivity, setPreviewActivity] = useState<PreviewActivity | null>(null);

  const activity = message?.included?.activity;
  const exerciseId = activity?.exercise?.id;
  const canPreview = activity?.activity_name === "exercise" && !!exerciseId;

  const getActivityTitle = () => {
    if (!message?.included?.activity?.activity_name) {
      return "";
    }

    if (isTranslatedActivity(message?.included?.activity?.activity_name)) {
      return message?.included?.activity?.translations?.title;
    }

    switch (message?.included?.activity?.activity_name) {
      case "followup_health_report":
        return t("messages.comment_types.follow_up");
      case "w1_health_report":
        return t("messages.comment_types.health");
      case "weekly_health_report":
        return t("messages.comment_types.weekly_report");
      default:
        return "";
    }
  };

  return (
    <>
      <ChatBubble className="outbound">
        <ActivityContainer>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <ActivityIcon type={getActivityType(activity?.activity_name)} />
              <Typography variant="subtitle2">{getActivityTitle()}</Typography>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              {activity?.activity_name === "exercise" && (
                <Typography variant="subtitle1">{`${activity?.exercise?.current_level}/${
                  (activity?.exercise?.levels?.length || 2) - 1
                }`}</Typography>
              )}
              {canPreview && (
                <BaseButton
                  variant="action"
                  icon="eye"
                  size="small"
                  title={t("treatment.editor.preview_activity", { activityType: "exercise" })}
                  onClick={() => {
                    if (exerciseId == null) return;
                    setPreviewActivity({
                      id: exerciseId,
                      type: "exercise",
                      part: activity?.exercise?.current_level || 1,
                    });
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle1">{t("messages.comment_title")}</Typography>
            <ChatBubble className="inbound">{message?.included?.activity?.comment}</ChatBubble>
          </Stack>
        </ActivityContainer>

        <Stack spacing={2}>
          <Typography color="#fff">{message?.body}</Typography>
          <Typography variant="label" color="#fff" sx={{ alignSelf: "flex-end" }}>
            <RelativeDatetime dateString={message.created_at} />
          </Typography>
        </Stack>
      </ChatBubble>

      {previewActivity && <ActivityPreviewModal activity={previewActivity} onClose={() => setPreviewActivity(null)} />}
    </>
  );
};

const ActivityContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
`;
